import type { FormField } from '@re/form-engine';
import { DynamicForm } from '@re/form-ui';
import { Box, createTheme, FormControlLabel, Stack, Switch, ThemeProvider, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { loadLocations } from '../../api/lookups';
import type { usePlatformLookups } from '../../api/lookups';
import { Button } from '../../components/button';
import { isEn, tr } from '../../lib/i18n';

/** The form exactly as customers get it (same engine), with a switch for the content language. */
export function FormPreviewPane({ fields, lookups, phone: asPhone = true }: { fields: FormField[]; lookups: NonNullable<ReturnType<typeof usePlatformLookups>['data']>; phone?: boolean }) {
  const [value, setValue] = useState<Record<string, unknown>>({});
  const [validate, setValidate] = useState(false);
  const [lang, setLang] = useState<'ar' | 'en'>(isEn() ? 'en' : 'ar');
  const schema = useMemo(() => ({ fields }), [fields]);
  // the preview is phone-sized: the form lays out as on a phone (every breakpoint above xs is out of reach)
  const outer = useTheme();
  const phone = useMemo(() => createTheme({ ...outer, breakpoints: { values: { xs: 0, sm: 100000, md: 100001, lg: 100002, xl: 100003 } } }), [outer]);
  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
        <FormControlLabel control={<Switch size="small" checked={validate} onChange={(e) => setValidate(e.target.checked)} />} label={tr('أخطاء التحقق', 'Validation errors')} sx={{ flex: 1, minWidth: 150, mx: 0 }} />
        <ToggleButtonGroup size="small" exclusive value={lang} onChange={(_, v) => v && setLang(v)} aria-label={tr('لغة النموذج', 'Form language')}>
          <ToggleButton value="ar" sx={{ px: 1.25, py: 0.25 }}>ع</ToggleButton>
          <ToggleButton value="en" sx={{ px: 1.25, py: 0.25 }}>EN</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" onClick={() => setValue({})}>{tr('تفريغ', 'Clear')}</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">{tr('جرّب الاختيارات لرؤية الحقول الشرطية — نفس المنطق الذي يطبقه الخادم.', 'Try the choices to see conditional fields — the same logic the server applies.')}</Typography>
      <ThemeProvider theme={asPhone ? phone : outer}>
        <Box sx={{ maxWidth: asPhone ? 420 : undefined, width: '100%', mx: 'auto' }}>
          <DynamicForm schema={schema} lookups={lookups} value={value} onChange={setValue} showValidation={validate} loadLocations={loadLocations} lang={lang} />
        </Box>
      </ThemeProvider>
    </Stack>
  );
}
